import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FaceGuide } from "@/components/FaceGuide";
import { Camera, CheckCircle2, FileImage, Loader2, RotateCcw, SkipForward, SwitchCamera, Upload, Users, XCircle } from "lucide-react";
import { toast } from "sonner";

const SIZE = 480;
const toSquare = (src, w, h) => {
  const c = document.createElement("canvas"); c.width = SIZE; c.height = SIZE;
  const side = Math.min(w, h);
  c.getContext("2d").drawImage(src, (w - side) / 2, (h - side) / 2, side, side, 0, 0, SIZE, SIZE);
  return c.toDataURL("image/jpeg", 0.85);
};
const fileToSquare = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file); const img = new Image();
  img.onload = () => { resolve(toSquare(img, img.naturalWidth, img.naturalHeight)); URL.revokeObjectURL(url); };
  img.onerror = () => { reject(new Error("Arquivo de imagem inválido")); URL.revokeObjectURL(url); };
  img.src = url;
});
const enrollOne = async (s, image) => {
  try { await api.post(`/students/${s.id}/face-enroll`, { image_base64: image }); return { status: "OK" }; }
  catch (e) { return { status: "FAIL", error: formatApiError(e.response?.data?.detail) || "Falha no envio" }; }
};
const stem = (n) => n.replace(/\.[^.]+$/, "").trim().toLowerCase();

const ProgressBar = ({ total, results }) => {
  const v = Object.values(results);
  const ok = v.filter((r) => r.status === "OK").length, fail = v.filter((r) => r.status === "FAIL").length, skip = v.filter((r) => r.status === "SKIP").length;
  return (
    <div className="mb-4" data-testid="bulk-progress">
      <div className="mb-1 flex flex-wrap gap-3 text-sm"><b data-testid="bulk-progress-text">{ok}/{total} alunos cadastrados</b>
        <span className="text-rose-600">{fail} falha(s)</span><span className="text-slate-500">{skip} pulado(s)</span></div>
      <Progress value={total ? ((ok + fail + skip) / total) * 100 : 0} />
    </div>
  );
};

const CaptureStep = ({ student, index, total, onResult }) => {
  const videoRef = useRef(null); const streamRef = useRef(null);
  const [preview, setPreview] = useState(null); const [busy, setBusy] = useState(false); const [error, setError] = useState(null);
  const [facing, setFacing] = useState("environment");
  const stop = useCallback(() => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; }, []);
  useEffect(() => {
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: { ideal: facing } } }).then((s) => {
      streamRef.current = s; if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
    }).catch(() => toast.error("Não foi possível acessar a câmera — use o envio de arquivo"));
    return () => stop();
  }, [facing, stop]);
  useEffect(() => { setPreview(null); setError(null); if (videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current; }, [student.id]);
  const snap = () => { const v = videoRef.current; if (v?.videoWidth) setPreview(toSquare(v, v.videoWidth, v.videoHeight)); };
  const onFile = async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { try { setPreview(await fileToSquare(f)); } catch (x) { toast.error(x.message); } } };
  const send = async () => {
    setBusy(true); setError(null);
    const r = await enrollOne(student, preview);
    setBusy(false);
    if (r.status === "OK") onResult(r); else { setError(r.error); setPreview(null); }
  };
  return (
    <div className="grid gap-4 sm:grid-cols-[320px_1fr]" data-testid="bulk-capture">
      <div className="relative overflow-hidden rounded-xl border bg-black">
        {preview ? <img src={preview} alt="prévia" className="aspect-square w-full object-cover" data-testid="bulk-preview" />
          : <video ref={videoRef} autoPlay muted playsInline className="aspect-square w-full object-cover" />}
        {!preview && <FaceGuide />}
      </div>
      <div className="space-y-3">
        <div className="text-xs text-slate-500">Aluno {index + 1} de {total}</div>
        <div className="font-display text-lg font-semibold" data-testid="bulk-current-name">{student.full_name}</div>
        <div className="text-xs text-slate-500">Matrícula {student.registration || "—"}</div>
        {error && <p className="rounded bg-rose-50 p-2 text-sm text-rose-700" data-testid="bulk-error">{error}. Tente outra foto ou pule.</p>}
        <div className="flex flex-wrap gap-2">
          {!preview && <Button variant="outline" onClick={snap} data-testid="bulk-snap"><Camera className="mr-1 h-4 w-4" />Capturar</Button>}
          {!preview && <Button variant="outline" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} data-testid="bulk-switch-camera">
            <SwitchCamera className="mr-1 h-4 w-4" />{facing === "environment" ? "Usar frontal" : "Usar traseira"}</Button>}
          <label className="inline-flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm hover:bg-slate-50"><Upload className="mr-1 h-4 w-4" />Enviar foto
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="bulk-file-one" /></label>
          {preview && <Button variant="outline" onClick={() => setPreview(null)} data-testid="bulk-retake"><RotateCcw className="mr-1 h-4 w-4" />Refazer</Button>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="bg-blue-700 hover:bg-blue-800" disabled={!preview || busy} onClick={send} data-testid="bulk-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1 h-4 w-4" />Cadastrar e avançar</>}</Button>
          <Button variant="ghost" disabled={busy} onClick={() => onResult(error ? { status: "FAIL", error } : { status: "SKIP" })} data-testid="bulk-skip"><SkipForward className="mr-1 h-4 w-4" />Pular</Button>
        </div>
      </div>
    </div>
  );
};

const FilesStep = ({ all, pending, onDone }) => {
  const [busy, setBusy] = useState(false); const [count, setCount] = useState(null);
  const run = async (e) => {
    const files = [...(e.target.files || [])]; e.target.value = "";
    if (!files.length) return;
    const byReg = Object.fromEntries(all.filter((s) => s.registration).map((s) => [String(s.registration).trim().toLowerCase(), s]));
    const pend = new Set(pending.map((s) => s.id));
    const results = {}; const orphans = [];
    setBusy(true);
    for (let i = 0; i < files.length; i += 1) {
      setCount(`${i + 1}/${files.length}`);
      const s = byReg[stem(files[i].name)];
      if (!s) { orphans.push({ file: files[i].name, error: "Nenhum aluno da turma com essa matrícula" }); continue; }
      if (!pend.has(s.id)) { orphans.push({ file: files[i].name, error: `${s.full_name} já tem biometria` }); continue; }
      try { results[s.id] = await enrollOne(s, await fileToSquare(files[i])); } catch (x) { results[s.id] = { status: "FAIL", error: x.message }; }
    }
    setBusy(false); onDone(results, orphans);
  };
  return (
    <div className="rounded-xl border border-dashed p-6 text-center" data-testid="bulk-files">
      <FileImage className="mx-auto mb-2 h-8 w-8 text-slate-400" />
      <p className="mb-3 text-sm text-slate-600">Selecione várias fotos nomeadas pela matrícula do aluno (ex.: <b>2026001.jpg</b>). Cada foto é enviada ao CompreFace automaticamente.</p>
      {busy ? <div className="text-sm" data-testid="bulk-files-progress"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" />Enviando {count}…</div>
        : <label className="inline-flex cursor-pointer items-center rounded-md bg-blue-700 px-4 py-2 text-sm text-white hover:bg-blue-800"><Upload className="mr-1 h-4 w-4" />Escolher fotos
          <input type="file" accept="image/*" multiple className="hidden" onChange={run} data-testid="bulk-files-input" /></label>}
    </div>
  );
};

const Summary = ({ students, results, orphans, onRetry, onClose, blocked }) => {
  const v = Object.values(results);
  const fails = students.filter((s) => results[s.id]?.status === "FAIL");
  return (
    <div data-testid="bulk-summary">
      <div className="mb-3 flex flex-wrap gap-2">
        <Badge className="bg-green-100 text-green-700" data-testid="bulk-summary-ok">{v.filter((r) => r.status === "OK").length} cadastrado(s)</Badge>
        <Badge className="bg-rose-100 text-rose-700" data-testid="bulk-summary-fail">{fails.length} falha(s)</Badge>
        <Badge className="bg-slate-100 text-slate-600" data-testid="bulk-summary-skip">{v.filter((r) => r.status === "SKIP").length} pulado(s)</Badge>
      </div>
      {[...fails.map((s) => ({ k: s.id, name: s.full_name, error: results[s.id].error })), ...orphans.map((o) => ({ k: o.file, name: o.file, error: o.error }))].map((r) => (
        <div key={r.k} className="flex items-start gap-2 border-b py-1.5 text-sm last:border-0"><XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" /><b>{r.name}</b><span className="text-slate-600">— {r.error}</span></div>))}
      <div className="mt-4 flex gap-2">
        {fails.length > 0 && <Button disabled={blocked} onClick={() => onRetry(fails)} data-testid="bulk-retry"><RotateCcw className="mr-1 h-4 w-4" />Tentar de novo os que falharam</Button>}
        <Button variant="outline" onClick={onClose} data-testid="bulk-close">Concluir</Button>
      </div>
    </div>
  );
};

export const BulkFaceEnroll = ({ scopeParams, blocked }) => {
  const [classes, setClasses] = useState([]); const [classId, setClassId] = useState("");
  const [all, setAll] = useState([]); const [queue, setQueue] = useState([]);
  const [step, setStep] = useState("list"); const [idx, setIdx] = useState(0);
  const [results, setResults] = useState({}); const [orphans, setOrphans] = useState([]); const [total, setTotal] = useState(0);
  useEffect(() => { api.get("/classes", { params: scopeParams }).then((r) => setClasses(r.data)).catch(() => setClasses([])); }, [scopeParams]);
  const load = useCallback(async (cid) => {
    const { data } = await api.get("/students", { params: { ...scopeParams, class_id: cid, status: "MATRICULADO" } });
    setAll(data); setQueue(data.filter((s) => !s.face_enrolled)); setStep("list"); setResults({}); setOrphans([]); setIdx(0);
  }, [scopeParams]);
  useEffect(() => { if (classId) load(classId); }, [classId, load]);
  const onResult = (r) => {
    const next = { ...results, [queue[idx].id]: r }; setResults(next);
    if (idx + 1 < queue.length) setIdx(idx + 1); else setStep("summary");
  };
  const retry = (fails) => { setQueue(fails); setResults(Object.fromEntries(Object.entries(results).filter(([, r]) => r.status !== "FAIL"))); setIdx(0); setStep("capture"); };
  return (
    <Card className="mt-6 border-slate-200 p-5" data-testid="bulk-enroll">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Users className="h-5 w-5 text-blue-700" /><h3 className="font-semibold text-slate-800">Cadastro em massa</h3>
        <div className="w-64"><Select value={classId} onValueChange={setClassId}>
          <SelectTrigger data-testid="bulk-class-select"><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
      </div>
      {classId && step !== "list" && <ProgressBar total={total} results={results} />}
      {classId && step === "list" && (
        <div data-testid="bulk-list">
          <p className="mb-2 text-sm text-slate-600" data-testid="bulk-pending-count">{queue.length} de {all.length} aluno(s) sem biometria nesta turma.</p>
          <div className="mb-3 max-h-56 overflow-y-auto rounded border">{queue.map((s) => <div key={s.id} className="border-b px-3 py-1.5 text-sm last:border-0" data-testid={`bulk-pending-${s.id}`}>{s.full_name} <span className="text-xs text-slate-400">· {s.registration || "sem matrícula"}</span></div>)}
            {!queue.length && <p className="p-3 text-sm text-green-700">Todos os alunos já têm biometria.</p>}</div>
          {queue.length > 0 && <div className="flex flex-wrap gap-2">
            <Button className="bg-blue-700 hover:bg-blue-800" disabled={blocked} onClick={() => { setTotal(queue.length); setStep("capture"); }} data-testid="bulk-start-capture"><Camera className="mr-1 h-4 w-4" />Capturar um a um</Button>
            <Button variant="outline" disabled={blocked} onClick={() => { setTotal(queue.length); setStep("files"); }} data-testid="bulk-start-files"><FileImage className="mr-1 h-4 w-4" />Enviar várias fotos (nome = matrícula)</Button></div>}
        </div>
      )}
      {step === "capture" && queue[idx] && <CaptureStep student={queue[idx]} index={idx} total={queue.length} onResult={onResult} />}
      {step === "files" && <FilesStep all={all} pending={queue} onDone={(r, o) => { setResults(r); setOrphans(o); setStep("summary"); }} />}
      {step === "summary" && <Summary students={all} results={results} orphans={orphans} onRetry={retry} blocked={blocked} onClose={() => load(classId)} />}
    </Card>
  );
};
